import { L_TOKEN } from '@/lib/constants'
import { axiosInstance, parseJwt, setToken } from '@/lib/helpers'
import { stores } from '@/stores/index'

function readInjectedToken(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const fromSession = window.sessionStorage.getItem('sn_webview_token')
    if (fromSession && fromSession !== 'null' && fromSession.length > 10) {
      return fromSession
    }
    const fromLocal = window.localStorage.getItem(L_TOKEN)
    if (fromLocal && fromLocal !== 'null' && fromLocal.length > 10) {
      return fromLocal
    }
  } catch {
    // ignore storage errors
  }
  return null
}

function waitForInjectedToken(timeoutMs = 2500): Promise<string | null> {
  const existing = readInjectedToken()
  if (existing) return Promise.resolve(existing)

  return new Promise(resolve => {
    const startedAt = Date.now()
    const timer = window.setInterval(() => {
      const token = readInjectedToken()
      if (token) {
        window.clearInterval(timer)
        resolve(token)
        return
      }
      if (Date.now() - startedAt >= timeoutMs) {
        window.clearInterval(timer)
        resolve(null)
      }
    }, 50)
  })
}

/**
 * Apply admin + organization to the Auth store the same way email login does.
 * Dashboard sends users to /onboarding when currentOrganization is missing —
 * seeding only the JWT is not enough.
 */
export function applyAuthSessionPayload(payload: any, fallbackToken?: string) {
  if (!payload) return false

  const token = payload.token || fallbackToken
  if (!token) return false

  document.cookie = `authtoken=${token}; path=/`
  setToken(token)

  try {
    window.sessionStorage.setItem('sn_webview_token', token)
    window.localStorage.setItem(L_TOKEN, token)
  } catch {
    // ignore
  }

  const organization = payload.organization
  const admin = payload.admin
  const role = payload.role
  const organizations = payload.organizations

  if (organizations?.length) {
    stores.auth.setOrganizations(organizations)
  }

  if (organization?.id) {
    stores.auth.setCurrentOrganization(organization)
  } else {
    // Last resort: decode JWT body (signed AdminOrganization includes nested org)
    const decoded = parseJwt(token)
    if (decoded?.organization?.id) {
      stores.auth.setCurrentOrganization(decoded.organization)
    }
  }

  if (admin) {
    stores.auth.setCurrentAdmin({ ...admin, role })
  } else {
    const decoded = parseJwt(token)
    if (decoded?.admin) {
      stores.auth.setCurrentAdmin({ ...decoded.admin, role: role || decoded.role })
    }
  }

  return Boolean(stores.auth.currentOrganization?.id)
}

/**
 * SchoolNotion mobile embed (?bootstrap=1): hydrate token + organization + admin
 * before any protected page (e.g. dashboard) mounts.
 */
export async function bootstrapWebViewAuthSession(): Promise<boolean> {
  if (typeof window === 'undefined') return false

  const params = new URLSearchParams(window.location.search)
  const bootstrap = params.get('bootstrap') === '1' || params.get('bootstrap') === 'true'
  if (!bootstrap) return false

  const injected = await waitForInjectedToken()
  if (!injected) return false

  // Immediately seed axios + local storage so RouteGuard / API calls work
  document.cookie = `authtoken=${injected}; path=/`
  setToken(injected)

  // Prefer JWT hydrate first so dashboard never flashes onboarding while network is in flight
  const decoded = parseJwt(injected)
  if (decoded?.organization?.id) {
    stores.auth.setCurrentOrganization(decoded.organization)
  }
  if (decoded?.admin) {
    stores.auth.setCurrentAdmin({ ...decoded.admin })
  }

  try {
    const { data } = await axiosInstance.get('/organization/verify-token-webview?bootstrap=1', {
      timeout: 25000,
      headers: {
        token: injected,
        Authorization: injected,
      },
    })

    if (data?.status === 200 && data?.data) {
      return applyAuthSessionPayload(data.data, injected)
    }
  } catch {
    // Fall through to JWT-only hydrate
  }

  return applyAuthSessionPayload(
    {
      token: injected,
      organization: decoded?.organization,
      admin: decoded?.admin,
    },
    injected,
  )
}
